import React, { useState, useEffect, createContext } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import FullPageLoader from "../../components/loaders/FullPageLoader";
import { validateUser } from "../../api/users";
import DisplayError from "./DisplayError";
import { getFallbackToken, getNAAToken, fallbackLoginModal } from "../../api/auth";
import { axiosApi } from "../../axios";
import LoginView from "./LoginView";
import TrackPageView from "./TrackPageView";
import QueryDebug from "./QueryDebug";
import { jwtDecode } from "jwt-decode";
import { TokenData } from "../../types/auth";

type Props = {
  children?: React.ReactNode;
};

const debugAuth = false;

export const AuthContext = createContext<AuthContextType>({
  appInfo: null,
  tokenData: null,
  setAppInfo: () => {},
  cleariManageAuthUrl: () => {},
});

type AppInfo = {
  licenseCheck: boolean;
  webApiVersion: string;
  functionAppVersion?: string;
  addInVersion: string;
  documentManagement: string;
  iManageAuthUrl: string;
  pauseExpiry: Date;
};

export type AuthContextType = {
  appInfo: AppInfo | null;
  tokenData: TokenData;
  setAppInfo: React.Dispatch<React.SetStateAction<AppInfo | null>>;
  cleariManageAuthUrl: () => void;
};

let cachedToken = null;
let tokenExpiryTime = null;

const AuthContainer: React.FC<Props> = (props) => {
  const { children } = props;
  const queryClient = useQueryClient();

  const [naaOverride, setNaaOverride] = useState(() =>
    Office.context.requirements.isSetSupported('NestedAppAuth', '1.0')
  )

  const naaTokenQuery = useQuery({
    queryKey: ["naaTokenQuery"],
    queryFn: () => getNAAToken(),
    retry: false,
    enabled: debugAuth && naaOverride,
  });

  const tokenQuery = useQuery({
    queryKey: ["tokenQuery"],
    queryFn: () => getFallbackToken(),
    retry: false,
  });

  const getFreshToken = async (): Promise<string> => {
    if (cachedToken && tokenExpiryTime && Date.now() < tokenExpiryTime) {
      return cachedToken;
    }
    // queryClient is a stable object (no stale closure issue), so getQueryData
    // always reads the live token — prevents a second dialog opening immediately
    // after the first auth completes when the Axios interceptor fires.
    const existingToken = queryClient.getQueryData<string>(['tokenQuery']);
    if (existingToken) {
      const decodedExisting = jwtDecode(existingToken);
      if (decodedExisting.exp * 1000 > Date.now()) {
        cachedToken = existingToken;
        tokenExpiryTime = decodedExisting.exp * 1000;
        return cachedToken;
      }
    }
    const token = await getFallbackToken();
    const decodedToken = jwtDecode(token);
    cachedToken = token;
    tokenExpiryTime = decodedToken.exp * 1000;
    return token;
  };

  const validateUserQuery = useQuery({
    queryKey: ["validateUserQuery"],
    queryFn: () => {
      const tokenData: TokenData = jwtDecode(tokenQuery.data);
      return validateUser(tokenData.oid);
    },
    retry: false,
    enabled: Boolean(tokenQuery.status === "success" && tokenQuery.data),
  });

  const [appInfo, setAppInfo] = useState<AppInfo | null>(null);
  const cleariManageAuthUrl = () => setAppInfo(prev => (prev ? { ...prev, iManageAuthUrl: "" } : prev));

  /**
   * Api token interceptor
   */
  useEffect(() => {
    const requestInterceptor = async (config) => {
      const newConfig = Object.assign(config, {});

      try {
        const token = await getFreshToken();
        newConfig.headers["Authorization"] = `Bearer ${token}`;
      } catch (err) {
        queryClient.removeQueries({ queryKey: ["tokenQuery"] });
        tokenQuery.refetch();
      }

      return newConfig;
    };

    const interceptor = axiosApi.interceptors.request.use(requestInterceptor);

    return () => axiosApi.interceptors.request.eject(interceptor);
  }, [queryClient]);

  useEffect(() => {
    if (validateUserQuery.isSuccess && validateUserQuery.data) {
      setAppInfo(validateUserQuery.data);
    }
  }, [validateUserQuery.data, validateUserQuery.isSuccess]);

  if (debugAuth) {
    return (
      <div>
        <button type='button' onClick={() => setNaaOverride(v => !v)}>
          NAA: {naaOverride ? 'enabled (click to disable)' : 'disabled (click to enable)'}
        </button>
        <button
          type='button'
          onClick={async () => {
            queryClient.removeQueries({ queryKey: ['tokenQuery'] })
            const token = await fallbackLoginModal()
            queryClient.setQueryData(['tokenQuery'], token)
          }}
        >
          Force fallback dialog
        </button>
        <QueryDebug query={naaTokenQuery} title="naaTokenQuery" refetch={naaTokenQuery.refetch} />
        <QueryDebug query={tokenQuery} title="tokenQuery" refetch={tokenQuery.refetch} />
        <QueryDebug query={validateUserQuery} title="validateUserQuery" refetch={validateUserQuery.refetch} />
      </div>
    );
  }

  if (tokenQuery.isPending) {
    return <FullPageLoader title="Authenticating..." />;
  }

  if (tokenQuery.isError) {
    console.log("Error in tokenQuery", tokenQuery.error);
    return (
      <>
        <DisplayError title="Login failed" />

        <LoginView
          title="Please log in with your microsoft account"
          btnTitle=""
          onBtnClick={() => {
            fallbackLoginModal().then((token) => {
              queryClient.setQueryData(["tokenQuery"], token);
            }).catch(() => {
              queryClient.removeQueries({ queryKey: ["tokenQuery"] });
              tokenQuery.refetch();
            });
          }}
        />
      </>
    );
  }

  if (validateUserQuery.isPending) {
    return <FullPageLoader title="Checking licence..." />;
  }

  if (validateUserQuery.isError) {
    const errorText = validateUserQuery.error?.toString() || "Unknown error";
    const errorMessageText = errorText.replace("Error:", "");
    return (
      <>
        <TrackPageView name="User validation error" />
        <DisplayError title="An error occurred while validating the user." errorMessage={errorMessageText} />
      </>
    );
  }

  if (validateUserQuery.isSuccess && validateUserQuery.data.licenseCheck === false) {
    return (
      <>
        <TrackPageView name="Your license has expired" />
        <DisplayError title="Your license has expired" />
      </>
    );
  }

  const tokenData: TokenData = jwtDecode(tokenQuery.data);

  return (
    <AuthContext.Provider
      value={{
        tokenData,
        appInfo,
        setAppInfo,
        cleariManageAuthUrl,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContainer;
